/**
 * TrainingHoursTab — per-employee approved in-service hours vs. the agency's yearly requirement.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Award, ChevronDown, ChevronRight, Download, GraduationCap, Loader2, RefreshCw, Settings2, UserPlus } from "lucide-react";
import AssignInserviceDialog from "@/components/AssignInserviceDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  met: { label: "Met", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  on_track: { label: "On track", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  behind: { label: "Behind", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
const BASIS = { anniversary: "Anniversary year (from hire date)", calendar: "Calendar year (Jan–Dec)", rolling: "Rolling 12 months" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const roleLabel = (r) => ({ pcw: "PCW", rn: "RN", case_manager: "Case Manager", hr_scheduler: "HR / Scheduler", scheduler: "Scheduler", admin: "Admin" }[(r || "").toLowerCase()] || (r || "").replace(/_/g, " "));

async function downloadEmployeeFile(employeeId, fileId, filename) {
  const res = await axios.get(`${API}/employees/${employeeId}/files/${fileId}`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function TrainingHoursTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState("all");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [assign, setAssign] = useState(null); // null | {preselected: [ids]}

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/training-hours/report`);
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load training hours");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/training-hours/report?format=csv`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `training-hours-${report?.as_of || "report"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Export failed"); }
  };

  const certificate = async (row, item) => {
    setBusy(item.file_id);
    try {
      let fileId = item.certificate_file_id;
      if (!fileId) {
        const res = await axios.post(`${API}/training-hours/certificates/${item.file_id}/regenerate`);
        fileId = res.data.certificate_file_id;
        toast.success("Certificate issued and emailed to the employee");
        load();
      }
      await downloadEmployeeFile(row.employee_id, fileId, `Certificate - ${item.title}.pdf`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Certificate unavailable");
    } finally { setBusy(null); }
  };

  const regenerate = async (row, item) => {
    setBusy(item.file_id);
    try {
      await axios.post(`${API}/training-hours/certificates/${item.file_id}/regenerate`);
      toast.success("Certificate regenerated and emailed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not regenerate"); } finally { setBusy(null); }
  };

  if (loading && !report) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!report) return null;
  const rows = report.rows.filter((r) => filter === "all" || r.status === filter);
  const s = report.summary;

  return (
    <div className="space-y-4" data-testid="training-hours-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 mr-auto">
          <GraduationCap className="w-5 h-5 text-teal-600" />
          <div>
            <h2 className="font-semibold text-slate-900">Training hours</h2>
            <p className="text-xs text-slate-500">{report.settings.required_hours_per_year} h required per year · {BASIS[report.settings.period_basis]} · as of {fmt(report.as_of)}</p>
          </div>
        </div>
        {["all", "behind", "on_track", "met"].map((k) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`training-filter-${k}`}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {k === "all" ? `All (${s.employees})` : `${STATUS[k].label} (${s[k]})`}
          </button>
        ))}
        <Button size="sm" onClick={() => setAssign({ preselected: [] })} className="gap-1 bg-teal-600 hover:bg-teal-700" data-testid="training-assign-bulk-btn"><UserPlus className="w-4 h-4" /> Assign in-service</Button>
        <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} className="gap-1" data-testid="training-settings-btn"><Settings2 className="w-4 h-4" /> Requirement</Button>
        <Button variant="outline" size="sm" onClick={exportCsv} className="gap-1" data-testid="training-export-csv-btn"><Download className="w-4 h-4" /> Export CSV</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 w-8" />
              <th className="px-4 py-2">Employee</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Training year</th>
              <th className="px-4 py-2 w-64">Hours</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Last in-service</th>
              <th className="px-4 py-2 w-24" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400 italic">No employees match</td></tr>}
            {rows.map((r) => {
              const st = STATUS[r.status];
              const isOpen = !!open[r.employee_id];
              return [
                <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [r.employee_id]: !isOpen }))} data-testid={`training-row-${r.employee_id}`}>
                  <td className="px-4 py-3 text-slate-400">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                  <td className="px-4 py-3 text-slate-600">{roleLabel(r.role)}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{fmt(r.period_start)} – {fmt(r.period_end)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Progress value={r.percent} className="h-2 flex-1" />
                      <span className="text-xs text-slate-700 whitespace-nowrap" data-testid={`training-hours-${r.employee_id}`}>{r.hours_done} / {r.hours_required} h</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline" className={st.cls} data-testid={`training-status-${r.employee_id}`}>{st.label}</Badge></td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.last_inservice_at)}</td>
                  <td className="px-2 py-3 text-right">
                    <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-teal-700" onClick={(e) => { e.stopPropagation(); setAssign({ preselected: [r.employee_id] }); }} data-testid={`training-assign-${r.employee_id}`}>
                      <UserPlus className="w-3 h-3" /> Assign
                    </Button>
                  </td>
                </tr>,
                isOpen && (
                  <tr key={`${r.employee_id}-items`} className="bg-slate-50/70">
                    <td />
                    <td colSpan={7} className="px-4 pb-4 pt-1">
                      {r.items.length === 0 ? <p className="text-xs text-slate-400 italic">No approved in-services yet</p> : (
                        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
                          {r.items.map((it) => (
                            <li key={it.file_id} className={`flex flex-wrap items-center gap-3 px-3 py-2 text-xs ${it.in_period ? "" : "opacity-60"}`} data-testid={`training-item-${it.file_id}`}>
                              <Award className="w-3.5 h-3.5 text-amber-500" />
                              <span className="font-medium text-slate-800">{it.title}</span>
                              <span className="text-slate-500">{it.hours} h · completed {fmt(it.completed_at)}{it.in_period ? "" : " · outside this training year"}</span>
                              <span className="ml-auto flex gap-1">
                                <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" disabled={busy === it.file_id} onClick={(e) => { e.stopPropagation(); certificate(r, it); }} data-testid={`training-cert-${it.file_id}`}>
                                  {busy === it.file_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} {it.certificate_file_id ? "Certificate" : "Issue certificate"}
                                </Button>
                                {it.certificate_file_id && (
                                  <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-slate-500" disabled={busy === it.file_id} onClick={(e) => { e.stopPropagation(); regenerate(r, it); }} data-testid={`training-cert-regen-${it.file_id}`}>
                                    <RefreshCw className="w-3 h-3" /> Regenerate
                                  </Button>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      {assign && (
        <AssignInserviceDialog
          open
          onClose={() => setAssign(null)}
          employees={assign.preselected.length === 1 ? report.rows.filter((r) => r.employee_id === assign.preselected[0]).map((r) => ({ id: r.employee_id, name: r.name, role: r.role })) : report.rows.map((r) => ({ id: r.employee_id, name: r.name, role: r.role }))}
          preselected={assign.preselected}
        />
      )}
      <TrainingSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={report.settings} onSaved={load} />
    </div>
  );
}

function TrainingSettingsDialog({ open, onClose, settings, onSaved }) {
  const [hours, setHours] = useState(settings.required_hours_per_year);
  const [basis, setBasis] = useState(settings.period_basis);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setHours(settings.required_hours_per_year); setBasis(settings.period_basis); }, [settings, open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/training-hours/settings`, { required_hours_per_year: Number(hours), period_basis: basis });
      toast.success("Training requirement saved");
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="training-settings-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>In-service requirement</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Required hours per year</Label>
            <Input type="number" min="0" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="training-required-hours" />
          </div>
          <div>
            <Label className="text-xs">Training year</Label>
            <Select value={basis} onValueChange={setBasis}>
              <SelectTrigger data-testid="training-period-basis"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(BASIS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="training-settings-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
